/**
 * Phase 3J.10A · Common FG Warehouse Receipt page
 * PACKING → FG WAREHOUSE
 * Combines Domestic (PS-*) + Export (EPS-*) packing slips into one
 * pending list. Read-only until user explicitly clicks Receive.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PackageIcon, ArrowRightIcon, CheckCircleIcon, ClockCounterClockwiseIcon } from "@phosphor-icons/react";
import { OrderRef } from "@/components/StockBadge";
import FGCustomerSlip from "./FGCustomerSlip";

const btn = "h-8 px-3 bg-[var(--accent)] text-black text-[11px] font-mono uppercase tracking-widest rounded-sm flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed";
const btnAlt = "h-8 px-3 border border-[var(--border)] text-zinc-200 bg-transparent text-[11px] font-mono uppercase tracking-widest rounded-sm flex items-center gap-1.5";
const inp = "h-8 px-2 bg-[var(--bg-2)] border border-[var(--border)] rounded-sm text-xs font-mono focus:outline-none focus:border-[var(--accent)]";
const fmt = (v) => v == null ? "—" : (typeof v === "number" ? v.toLocaleString("en-IN", { maximumFractionDigits: 3 }) : String(v));

const SourceBadge = ({ type }) => {
  const isExport = type === "EXPORT_PS";
  return (
    <span data-testid={`fg-src-${type.toLowerCase()}`} className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest rounded-sm ${isExport ? "bg-purple-500/10 text-purple-300 border border-purple-500/50" : "bg-sky-500/10 text-sky-300 border border-sky-500/50"}`}>
      {isExport ? "EXPORT" : "DOMESTIC"}
    </span>
  );
};

export default function FGWarehouseReceipt() {
  const [tab, setTab] = useState("pending");
  const [source, setSource] = useState("ALL");
  const [slipNo, setSlipNo] = useState("");
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [warehouses, setWarehouses] = useState([]);
  const [destWh, setDestWh] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [counts, setCounts] = useState({ domestic_count: 0, export_count: 0 });

  const loadPending = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/fg-warehouse-receipt/pending", {
        params: { source, slip_no: slipNo || undefined },
      });
      setRows(data.rows || []);
      setCounts({ domestic_count: data.domestic_count || 0, export_count: data.export_count || 0 });
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load pending list");
    } finally { setLoading(false); }
  };

  const loadHistory = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/fg-warehouse-receipt/history", {
        params: { source },
      });
      setHistory(data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load history");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/godowns/");
        const list = Array.isArray(data) ? data : (data.rows || []);
        const fg = list.filter(g => (g.kind || "").toUpperCase() === "FG" && g.is_active);
        setWarehouses(fg);
        if (fg.length > 0 && !destWh) setDestWh(fg[0].id);
      } catch (e) { /* silent — user can still trigger reload */ }
    })();
  }, []);

  useEffect(() => {
    if (tab === "pending") loadPending(); else loadHistory();
     
  }, [tab, source]);

  const toggle = (key) => {
    const next = new Set(selected);
    next.has(key) ? next.delete(key) : next.add(key);
    setSelected(next);
  };

  const selectAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map(rowKey)));
  };

  const rowKey = (r) => `${r.source_type}::${r.source_document_id}::${r.source_line_id}`;

  const selectedRows = useMemo(() => rows.filter(r => selected.has(rowKey(r))), [rows, selected]);
  const totalQty = selectedRows.reduce((s, r) => s + (r.packed_qty || 0), 0);

  const doReceive = async () => {
    if (!destWh) { toast.error("Pick a destination FG warehouse"); return; }
    if (selectedRows.length === 0) { toast.error("Select at least one Packing Slip line"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/inventory/fg-warehouse-receipt/receive", {
        destination_warehouse_id: destWh,
        lines: selectedRows.map(r => ({
          source_type: r.source_type,
          source_document_id: r.source_document_id,
          source_document_no: r.source_document_no,
          source_line_id: r.source_line_id,
        })),
      });
      toast.success(`Received ${data.num_lines} line(s) → ${data.fgtr_no}`);
      await loadPending();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Receive failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="p-4 space-y-4" data-testid="fg-receipt-page">
      <div className="flex items-baseline gap-3">
        <h1 className="text-lg font-mono uppercase tracking-widest">PACKING → FG WAREHOUSE</h1>
        <span className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">Common Domestic + Export FG Receipt</span>
      </div>

      <div className="flex gap-2">
        <button data-testid="fg-tab-pending" onClick={() => setTab("pending")}
          className={`h-8 px-3 text-[11px] font-mono uppercase tracking-widest rounded-sm ${tab === "pending" ? "bg-[var(--accent)] text-black" : "border border-[var(--border)] text-zinc-300"}`}>
          <PackageIcon size={12} weight="bold" className="inline mr-1" />Pending Receipt
        </button>
        <button data-testid="fg-tab-history" onClick={() => setTab("history")}
          className={`h-8 px-3 text-[11px] font-mono uppercase tracking-widest rounded-sm ${tab === "history" ? "bg-[var(--accent)] text-black" : "border border-[var(--border)] text-zinc-300"}`}>
          <ClockCounterClockwiseIcon size={12} weight="bold" className="inline mr-1" />Received History
        </button>
        <button data-testid="fg-tab-create-from-fg" onClick={() => setTab("fromfg")}
          className={`h-8 px-3 text-[11px] font-mono uppercase tracking-widest rounded-sm ${tab === "fromfg" ? "bg-[var(--accent)] text-black" : "border border-[var(--border)] text-zinc-300"}`}>
          Create from FG Stock
        </button>
      </div>

      {tab === "fromfg" && <FGCustomerSlip />}
      {tab !== "fromfg" && (<>
      <div className="tactical-card p-3 flex items-end gap-2 flex-wrap">
        <div>
          <label className="overline block mb-1">Source</label>
          <div className="flex gap-1">
            {["ALL", "DOMESTIC", "EXPORT"].map(s => (
              <button key={s} data-testid={`fg-src-filter-${s.toLowerCase()}`} onClick={() => setSource(s)}
                className={`h-8 px-3 text-[11px] font-mono uppercase tracking-widest rounded-sm ${source === s ? "bg-[var(--accent)] text-black" : "border border-[var(--border)] text-zinc-300"}`}>{s}</button>
            ))}
          </div>
        </div>
        {tab === "pending" && (
          <>
            <div>
              <label className="overline block mb-1">Slip No</label>
              <input data-testid="fg-filter-slipno" value={slipNo} onChange={e => setSlipNo(e.target.value)} className={inp} placeholder="PS/EPS…" />
            </div>
            <div>
              <label className="overline block mb-1">Destination FG Warehouse</label>
              <select data-testid="fg-dest-warehouse" value={destWh} onChange={e => setDestWh(e.target.value)} className={inp}>
                {warehouses.map(w => (<option key={w.id} value={w.id}>{w.code} · {w.name}</option>))}
              </select>
            </div>
            <button data-testid="fg-reload" onClick={loadPending} className={btnAlt}>Reload</button>
          </>
        )}
      </div>

      {tab === "pending" && (
        <>
          <div className="flex items-center gap-3 text-[11px] font-mono">
            <span className="text-zinc-400" data-testid="fg-summary">
              Pending: <span className="text-zinc-100">{rows.length}</span> · Domestic: <span className="text-sky-300">{counts.domestic_count}</span> · Export: <span className="text-purple-300">{counts.export_count}</span>
            </span>
            <span className="text-zinc-500">·</span>
            <span className="text-zinc-400">Selected: <span className="text-emerald-300" data-testid="fg-selected-count">{selected.size}</span> · Total Qty: <span className="text-emerald-300">{fmt(totalQty)} m</span></span>
            <div className="flex-1" />
            <button data-testid="fg-select-all" onClick={selectAll} className={btnAlt}>{selected.size === rows.length && rows.length > 0 ? "Deselect All" : "Select All"}</button>
            <button data-testid="fg-receive" onClick={doReceive} disabled={selected.size === 0 || !destWh || loading} className={btn}>
              <ArrowRightIcon size={12} weight="bold" />Receive to FG Warehouse
            </button>
          </div>

          <div className="tactical-card overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead className="bg-[var(--bg-2)]">
                <tr className="text-left overline">
                  <th className="p-2 w-8"></th>
                  <th className="p-2">Source</th>
                  <th className="p-2">Packing Slip</th>
                  <th className="p-2">Date</th>
                  <th className="p-2">Order</th>
                  <th className="p-2">Customer</th>
                  <th className="p-2">Product</th>
                  <th className="p-2">Style</th>
                  <th className="p-2 text-right">Qty</th>
                  <th className="p-2">UOM</th>
                  <th className="p-2 text-right">Net Wt</th>
                  <th className="p-2 text-right">Gross Wt</th>
                  <th className="p-2">Barcode</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const k = rowKey(r);
                  return (
                    <tr key={k} className="border-t border-[var(--border)]" data-testid={`fg-pending-row-${i}`}>
                      <td className="p-2"><input type="checkbox" data-testid={`fg-row-check-${i}`} checked={selected.has(k)} onChange={() => toggle(k)} /></td>
                      <td className="p-2"><SourceBadge type={r.source_type} /></td>
                      <td className="p-2 text-zinc-100" data-testid={`fg-row-slipno-${i}`}>{r.source_document_no}</td>
                      <td className="p-2">{(r.packing_date || "").slice(0, 10)}</td>
                      <td className="p-2"><OrderRef no={r.order_no} stock={r.order_is_stock} testId={`fg-pending-order-${r.source_line_id}`} /></td>
                      <td className="p-2">{r.customer_name || <span className="text-zinc-500">—</span>}</td>
                      <td className="p-2">{r.product_code}</td>
                      <td className="p-2">{r.style_no || <span className="text-zinc-500">—</span>}</td>
                      <td className="p-2 text-right">{fmt(r.packed_qty)}</td>
                      <td className="p-2">{r.uom}</td>
                      <td className="p-2 text-right">{fmt(r.net_weight)}</td>
                      <td className="p-2 text-right">{fmt(r.gross_weight)}</td>
                      <td className="p-2 text-zinc-400">{r.barcode}</td>
                    </tr>
                  );
                })}
                {rows.length === 0 && !loading && (
                  <tr><td colSpan={13} className="p-4 text-center text-zinc-500">No pending Packing Slips for this filter</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "history" && (
        <div className="space-y-2">
          {history.length === 0 && !loading && (
            <div className="tactical-card p-4 text-center text-zinc-500 text-xs font-mono">No FG Receipts posted yet</div>
          )}
          {history.map((h, i) => (
            <div key={h.id} className="tactical-card p-3" data-testid={`fg-history-row-${i}`}>
              <div className="flex items-center gap-3 text-xs font-mono mb-2">
                <CheckCircleIcon size={16} weight="bold" className="text-emerald-400" />
                <span className="text-zinc-100 font-bold" data-testid={`fg-history-fgtr-${i}`}>{h.fgtr_no}</span>
                <span className="text-zinc-500">·</span>
                <span>{h.receipt_date}</span>
                <span className="text-zinc-500">·</span>
                <span>{h.destination_warehouse_code} · {h.destination_warehouse_name}</span>
                <span className="text-zinc-500">·</span>
                <span>{h.num_lines} lines</span>
                {h.domestic_line_count > 0 && (<span className="text-sky-300">· {h.domestic_line_count} Domestic</span>)}
                {h.export_line_count > 0 && (<span className="text-purple-300">· {h.export_line_count} Export</span>)}
                <div className="flex-1" />
                <span className="text-zinc-500">by {h.received_by}</span>
              </div>
              <table className="w-full text-xs font-mono">
                <thead className="bg-[var(--bg-2)]">
                  <tr className="text-left overline">
                    <th className="p-1.5">Source</th><th className="p-1.5">Packing Slip</th>
                    <th className="p-1.5">Product</th><th className="p-1.5 text-right">Qty</th>
                    <th className="p-1.5">UOM</th><th className="p-1.5">Barcode</th>
                  </tr>
                </thead>
                <tbody>
                  {(h.lines || []).map((l, j) => (
                    <tr key={j} className="border-t border-[var(--border)]">
                      <td className="p-1.5"><SourceBadge type={l.source_type} /></td>
                      <td className="p-1.5">{l.source_document_no}</td>
                      <td className="p-1.5">{l.product_code}</td>
                      <td className="p-1.5 text-right">{fmt(l.qty)}</td>
                      <td className="p-1.5">{l.uom}</td>
                      <td className="p-1.5 text-zinc-400">{l.barcode}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
      </>)}
    </div>
  );
}
